import { Grid } from 'antd';

/**
 * One layout tier for every screen, by window (CSS) width — never by device type:
 * - phone: below AntD `md` (768 px): phones 360–414 px and laptops at extreme zoom;
 * - tablet: 768–991 px: tablets and laptops at 150–175% zoom (853, 731 px);
 * - desktop: AntD `lg` (992 px) and up.
 *
 * Lists switch to cards below desktop; dialogs become bottom sheets on phone only.
 */
export type ViewportTier = 'phone' | 'tablet' | 'desktop';

export const TABLET_MIN_WIDTH = 768;
export const DESKTOP_MIN_WIDTH = 992;
/** Wide desktop: room to pin a column in a wide table without hiding the rest. */
export const WIDE_DESKTOP_MIN_WIDTH = 1200;

export const viewportTierOf = (width: number): ViewportTier =>
  width >= DESKTOP_MIN_WIDTH ? 'desktop' : width >= TABLET_MIN_WIDTH ? 'tablet' : 'phone';

const windowWidth = () => (typeof window === 'undefined' ? DESKTOP_MIN_WIDTH : window.innerWidth);

/**
 * AntD's first breakpoint snapshot is empty, so the window width decides the first render (no
 * wrong shell mounted and swapped). Once AntD publishes `md`/`lg`, those decide.
 */
export function useViewportTier(): ViewportTier {
  const screens = Grid.useBreakpoint();
  const width = windowWidth();
  // Breakpoints nest: whatever AntD has published wins over the window width (below md is never lg).
  const md = screens.md ?? (screens.lg || width >= TABLET_MIN_WIDTH);
  const lg = md && (screens.lg ?? width >= DESKTOP_MIN_WIDTH);
  return lg ? 'desktop' : md ? 'tablet' : 'phone';
}

/** True on wide desktops (AntD `xl`, 1200 px): the only width where a table may pin a column. */
export function useWideDesktop(): boolean {
  const screens = Grid.useBreakpoint();
  return screens.xl ?? windowWidth() >= WIDE_DESKTOP_MIN_WIDTH;
}
